<template>
  <div class="modal-overlay" @click="closeModal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2 id="modal-title">Reset Password</h2>
        <button @click="closeModal" class="close-btn" aria-label="Close modal" type="button">&times;</button>
      </div>

      <form @submit.prevent="handleSubmit" class="reset-form">
        <Alert v-if="successMessage" variant="default" class="reset-alert success-alert">
          <AlertDescription>{{ successMessage }}</AlertDescription>
        </Alert>

        <Alert v-if="errors.general" variant="destructive" class="reset-alert">
          <AlertDescription>{{ errors.general }}</AlertDescription>
        </Alert>

        <div v-if="!submitted" class="form-content">
          <p class="description">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          <div class="form-group">
            <Label for="email">Email Address</Label>
            <Input
              id="email"
              v-model="email"
              type="email"
              required
              placeholder="Enter your email"
              :class="{ 'input-error': errors.email }"
              aria-describedby="email-error"
              :aria-invalid="!!errors.email"
            />
            <span v-if="errors.email" id="email-error" class="error-message" role="alert">{{ errors.email }}</span>
          </div>

          <Button
            type="submit"
            class="submit-btn"
            :disabled="loading"
            :aria-busy="loading"
          >
            <span v-if="loading">Sending...</span>
            <span v-else>Send Reset Link</span>
          </Button>
        </div>

        <div class="form-footer">
          <p>Remember your password?
            <a href="#" @click.prevent="switchToLogin">Sign in here</a>
          </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import api from '@/api'
import { API_ENDPOINTS } from '@/constants'

export default {
  name: 'ForgotPasswordModal',
  components: {
    Button,
    Input,
    Label,
    Alert,
    AlertDescription
  },
  data() {
    return {
      email: '',
      errors: {},
      loading: false,
      submitted: false,
      successMessage: ''
    }
  },
  methods: {
    async handleSubmit() {
      this.errors = {}
      this.loading = true
      this.successMessage = ''

      // Basic email validation
      if (!this.email) {
        this.errors.email = 'Email is required'
        this.loading = false
        return
      }

      try {
        await api.post(API_ENDPOINTS.PASSWORD_RESET_REQUEST, {
          email: this.email
        })

        this.submitted = true
        this.successMessage = 'If your email is registered, you will receive a password reset link shortly. Please check your email.'

        // Auto-close modal after 5 seconds
        setTimeout(() => {
          this.closeModal()
        }, 5000)

      } catch (error) {
        console.error('Password reset request error:', error)
        
        if (error.response?.data?.detail) {
          this.errors.general = error.response.data.detail
        } else {
          this.errors.general = 'Failed to send reset link. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },
    closeModal() {
      this.$emit('close')
    },
    switchToLogin() {
      this.$emit('close')
      this.$emit('show-login')
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #374151;
}

.reset-form {
  padding: 1.5rem;
}

.description {
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.form-content {
  margin-bottom: 1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.input-error {
  border-color: #ef4444 !important;
}

.error-message {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.submit-btn {
  width: 100%;
  background: #0e3b2e;
  color: white;
}

.submit-btn:hover:not(:disabled) {
  background: #134d3c;
}

.reset-alert {
  margin-bottom: 1rem;
}

.success-alert {
  background-color: #d1fae5;
  border-color: #10b981;
  color: #065f46;
}

.form-footer {
  text-align: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.form-footer a {
  color: #0e3b2e;
  text-decoration: none;
  font-weight: 500;
}

.form-footer a:hover {
  text-decoration: underline;
}

/* Mobile Responsive Styles */
@media (max-width: 480px) {
  .modal-content {
    width: 95%;
    max-width: 100%;
    margin: 1rem;
    border-radius: 8px;
  }

  .modal-header {
    padding: 1rem;
  }

  .modal-header h2 {
    font-size: 1.25rem;
  }

  .reset-form {
    padding: 1rem;
  }
}
</style>
